import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useApp, formatCurrency } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, MoreHorizontal, Pencil, Trash2, Calculator, TrendingUp, TrendingDown, Wallet, ArrowLeft, Receipt } from "lucide-react";
import { toast } from "sonner";
import { v4 as uuidv4 } from 'uuid';

// Local storage key for budget planner items only (categories come from main app)
const BUDGET_ITEMS_KEY = 'bkfinance_budget_items';

const loadBudgetData = (key, defaultValue) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const saveBudgetData = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error('Failed to save budget data:', e);
  }
};

// Add Category Dialog - adds to MAIN app categories
const AddCategoryDialog = ({ onSave }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("expense");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter a category name");
      return;
    }
    
    onSave({
      name: name.trim(),
      type
    });
    
    toast.success("Category added to app!");
    setOpen(false);
    setName("");
    setType("expense");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full">
          <Plus className="w-4 h-4 mr-2" />Add Category
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Add New Category</DialogTitle>
          <DialogDescription>This category will be available everywhere in the app.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Category Name</Label>
            <Input placeholder="e.g., Rent, Groceries" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="expense">Expense</SelectItem>
                <SelectItem value="income">Income</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="w-full rounded-full bg-stone-900 hover:bg-stone-800">
            Add Category
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Add Budget Item Dialog
const BudgetItemDialog = ({ item, categories, onSave, onClose }) => {
  const [open, setOpen] = useState(false);
  const [categoryId, setCategoryId] = useState(item?.categoryId || "");
  const [amount, setAmount] = useState(item?.amount?.toString() || "");
  const [notes, setNotes] = useState(item?.notes || "");

  const isEdit = !!item;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!categoryId || !amount) {
      toast.error("Please fill all required fields");
      return;
    }
    
    const category = categories.find(c => c.id === categoryId);
    
    onSave({
      id: item?.id || uuidv4(),
      categoryId,
      amount: parseFloat(amount),
      notes,
      type: category?.type || 'expense'
    });
    
    if (isEdit) {
      toast.success("Budget item updated!");
    }
    // Toast for new items is handled in the parent component
    
    setOpen(false);
    setCategoryId("");
    setAmount("");
    setNotes("");
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            <Pencil className="w-4 h-4 mr-2" />Edit
          </DropdownMenuItem>
        ) : (
          <Button className="rounded-full bg-stone-900 hover:bg-stone-800">
            <Plus className="w-4 h-4 mr-2" />Add Budget Item
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Budget Item" : "Add Budget Item"}</DialogTitle>
          <DialogDescription>Plan your expected income or expenses.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.type === 'income' ? '📈' : '📉'} {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Estimated Amount (£)</Label>
            <Input type="number" placeholder="0" value={amount} onChange={(e) => setAmount(e.target.value)} min="0" step="0.01" required />
          </div>
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Input placeholder="Add a note..." value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <Button type="submit" className="w-full rounded-full bg-stone-900 hover:bg-stone-800">
            {isEdit ? "Update" : "Add Item"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Delete Confirmation
const DeleteDialog = ({ title, description, onDelete }) => {
  const [open, setOpen] = useState(false);

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setOpen(true); }} className="text-rose-600">
        <Trash2 className="w-4 h-4 mr-2" />Delete
      </DropdownMenuItem>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => { onDelete(); setOpen(false); }} className="bg-rose-600 hover:bg-rose-700">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default function BudgetPlanner() {
  const navigate = useNavigate();
  
  // Use main app's categories, delete function, and bills
  const { categories, addCategory, deleteCategory, addBill, deleteBill, bills, selectedCycle } = useApp();
  
  const [budgetItems, setBudgetItems] = useState(() => 
    loadBudgetData(BUDGET_ITEMS_KEY, [])
  );

  const saveItems = (items) => {
    setBudgetItems(items);
    saveBudgetData(BUDGET_ITEMS_KEY, items);
  };

  // Add category to main app
  const handleAddCategory = (categoryData) => {
    addCategory(categoryData);
  };

  // Delete category from main app
  const handleDeleteCategory = (categoryId) => {
    // Also remove any budget items using this category
    saveItems(budgetItems.filter(i => i.categoryId !== categoryId));
    deleteCategory(categoryId);
    toast.success("Category deleted!");
  };

  // Item CRUD - also adds to Bills for both expense and income items
  const handleSaveItem = (item) => {
    const exists = budgetItems.find(i => i.id === item.id);
    const category = categories.find(c => c.id === item.categoryId);
    
    if (exists) {
      // Update existing item
      saveItems(budgetItems.map(i => i.id === item.id ? item : i));
    } else {
      // Add new item and also add to Bills section
      const newBill = addBill({
        name: category?.name || 'Budget Item',
        amount: item.amount,
        categoryId: item.categoryId,
        cycleKey: selectedCycle || 'default',
        isPaid: false,
        type: item.type, // 'income' or 'expense'
        notes: item.notes || 'From Budget Planner'
      });
      
      const linkedBillId = newBill?.id;
      
      // Save with linked bill ID
      saveItems([...budgetItems, { ...item, linkedBillId }]);
      
      toast.success(item.type === 'income' 
        ? "Added to Budget Planner & Income!" 
        : "Added to Budget Planner & Bills!"
      );
    }
  };

  // Sync existing budget items to Bills
  const handleSyncToBills = () => {
    const unsyncedItems = budgetItems.filter(i => !i.linkedBillId);
    
    if (unsyncedItems.length === 0) {
      toast.info("All items are already synced to Bills!");
      return;
    }
    
    const updatedItems = budgetItems.map(item => {
      if (!item.linkedBillId) {
        const category = categories.find(c => c.id === item.categoryId);
        const newBill = addBill({
          name: category?.name || 'Budget Item',
          amount: item.amount,
          categoryId: item.categoryId,
          cycleKey: selectedCycle || 'default',
          isPaid: false,
          type: item.type,
          notes: item.notes || 'From Budget Planner'
        });
        return { ...item, linkedBillId: newBill?.id };
      }
      return item;
    });
    
    saveItems(updatedItems);
    toast.success(`${unsyncedItems.length} item(s) synced to Bills!`);
  };

  const handleDeleteItem = (id) => {
    const item = budgetItems.find(i => i.id === id);
    
    // If it has a linked bill, delete that too
    if (item?.linkedBillId) {
      deleteBill(item.linkedBillId);
    }
    
    saveItems(budgetItems.filter(i => i.id !== id));
    toast.success("Budget item deleted!");
  };

  // Calculate totals
  const totalExpectedIncome = useMemo(() => {
    return budgetItems
      .filter(i => i.type === 'income')
      .reduce((sum, i) => sum + i.amount, 0);
  }, [budgetItems]);

  const totalExpectedExpenses = useMemo(() => {
    return budgetItems
      .filter(i => i.type === 'expense')
      .reduce((sum, i) => sum + i.amount, 0);
  }, [budgetItems]);

  const expectedBalance = totalExpectedIncome - totalExpectedExpenses;

  const getCategoryName = (categoryId) => {
    return categories.find(c => c.id === categoryId)?.name || "Unknown";
  };

  const incomeItems = budgetItems.filter(i => i.type === 'income');
  const expenseItems = budgetItems.filter(i => i.type === 'expense');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="budget-planner-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-violet-100">
            <Calculator className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <h2 className="font-heading text-3xl text-stone-900">Budget Planner</h2>
            <p className="text-stone-500">Plan & estimate your future finances</p>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <Card className="p-4 bg-violet-50 border-violet-200 rounded-xl">
        <p className="text-sm text-violet-800">
          💡 <strong>This is separate from your actual transactions.</strong> Use this to plan and estimate how much you'll need. Categories added here will also appear in the main Categories section.
        </p>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 bg-emerald-50 border-emerald-200 rounded-xl">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="text-xs font-semibold uppercase text-stone-500">Expected Income</p>
              <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalExpectedIncome)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-5 bg-rose-50 border-rose-200 rounded-xl">
          <div className="flex items-center gap-3">
            <TrendingDown className="w-5 h-5 text-rose-600" />
            <div>
              <p className="text-xs font-semibold uppercase text-stone-500">Expected Expenses</p>
              <p className="text-2xl font-bold text-rose-700">{formatCurrency(totalExpectedExpenses)}</p>
            </div>
          </div>
        </Card>
        <Card className={`p-5 rounded-xl ${expectedBalance >= 0 ? 'bg-indigo-50 border-indigo-200' : 'bg-rose-50 border-rose-200'}`}>
          <div className="flex items-center gap-3">
            <Wallet className={`w-5 h-5 ${expectedBalance >= 0 ? 'text-indigo-600' : 'text-rose-600'}`} />
            <div>
              <p className="text-xs font-semibold uppercase text-stone-500">Expected Balance</p>
              <p className={`text-2xl font-bold ${expectedBalance >= 0 ? 'text-indigo-700' : 'text-rose-700'}`}>
                {formatCurrency(expectedBalance)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <BudgetItemDialog categories={categories} onSave={handleSaveItem} />
        <AddCategoryDialog onSave={handleAddCategory} />
        {budgetItems.filter(i => !i.linkedBillId).length > 0 && (
          <Button 
            variant="outline" 
            className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={handleSyncToBills}
          >
            <Receipt className="w-4 h-4 mr-2" />
            Sync to Bills ({budgetItems.filter(i => !i.linkedBillId).length})
          </Button>
        )}
      </div>

      {/* No Categories Warning */}
      {categories.length === 0 && (
        <Card className="p-6 bg-amber-50 border-amber-200 rounded-xl">
          <p className="text-amber-700">
            No categories found. Add a category first to start planning your budget.
          </p>
        </Card>
      )}

      {/* Budget Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income Section */}
        <Card className="p-5 bg-white rounded-2xl border border-stone-200/60">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Expected Income
          </h3>
          {incomeItems.length > 0 ? (
            <div className="space-y-3">
              {incomeItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div>
                    <p className="font-medium text-stone-900">{getCategoryName(item.categoryId)}</p>
                    {item.notes && <p className="text-xs text-stone-500">{item.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-emerald-700">{formatCurrency(item.amount)}</p>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <BudgetItemDialog item={item} categories={categories} onSave={handleSaveItem} />
                        <DeleteDialog title="Delete Item" description="Are you sure?" onDelete={() => handleDeleteItem(item.id)} />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-stone-400 text-sm text-center py-6">No expected income added</p>
          )}
        </Card>

        {/* Expense Section */}
        <Card className="p-5 bg-white rounded-2xl border border-stone-200/60">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-rose-600" />
            Expected Expenses
          </h3>
          {expenseItems.length > 0 ? (
            <div className="space-y-3">
              {expenseItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-rose-50 rounded-xl border border-rose-100">
                  <div>
                    <p className="font-medium text-stone-900">{getCategoryName(item.categoryId)}</p>
                    {item.notes && <p className="text-xs text-stone-500">{item.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-rose-700">{formatCurrency(item.amount)}</p>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <BudgetItemDialog item={item} categories={categories} onSave={handleSaveItem} />
                        <DeleteDialog title="Delete Item" description="Are you sure?" onDelete={() => handleDeleteItem(item.id)} />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-stone-400 text-sm text-center py-6">No expected expenses added</p>
          )}
        </Card>
      </div>

      {/* Available Categories */}
      <Card className="p-5 bg-white rounded-2xl border border-stone-200/60">
        <h3 className="font-semibold text-stone-900 mb-4">Available Categories</h3>
        <p className="text-xs text-stone-500 mb-3">These categories are shared across the entire app</p>
        {categories.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <div key={cat.id} className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm ${
                cat.type === 'income' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
              }`}>
                {cat.type === 'income' ? '📈' : '📉'} {cat.name}
                <button 
                  onClick={() => handleDeleteCategory(cat.id)}
                  className={`ml-1 p-1 rounded-full hover:bg-white/50 ${
                    cat.type === 'income' ? 'text-emerald-600 hover:text-emerald-800' : 'text-rose-600 hover:text-rose-800'
                  }`}
                  title="Delete category"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-stone-400 text-sm">No categories yet. Add one above!</p>
        )}
      </Card>
    </div>
  );
}
